import { action, makeObservable, observable } from "mobx";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

const PDF_PAGE_LIMIT = 100;

export class PdfPageLimitWarningModalStore {
  isOpen: boolean = false;
  pageCount: number = 0;

  private pendingResolve: ((confirmed: boolean) => void) | null = null;
  private shownAt: number = 0;

  constructor() {
    makeObservable<this, "pendingResolve" | "shownAt">(this, {
      isOpen: observable,
      pageCount: observable,
      pageLimit: false,
      pendingResolve: false,
      shownAt: false,
      open: action,
      confirm: action,
      cancel: action,
    });
  }

  get pageLimit(): number {
    return PDF_PAGE_LIMIT;
  }

  /**
   * Opens the modal and returns a Promise that resolves to `true` if the user
   * clicks "Upload anyway", or `false` if they cancel / dismiss.
   */
  public open({ pageCount }: { pageCount: number }): Promise<boolean> {
    this.pageCount = pageCount;
    this.isOpen = true;
    this.shownAt = performance.now();

    trackEvent(TrackedEvent.PdfPageLimitWarningShown, {
      page_count: pageCount,
      page_limit: this.pageLimit,
    });

    return new Promise<boolean>(resolve => {
      this.pendingResolve = resolve;
    });
  }

  public confirm() {
    if (this.pendingResolve) {
      trackEvent(TrackedEvent.PdfPageLimitWarningConfirmed, {
        page_count: this.pageCount,
        page_limit: this.pageLimit,
        visible_duration_ms: Math.round(performance.now() - this.shownAt),
      });
    }

    this.pendingResolve?.(true);
    this.pendingResolve = null;
    this.isOpen = false;
  }

  public cancel() {
    if (this.pendingResolve) {
      trackEvent(TrackedEvent.PdfPageLimitWarningCancelled, {
        page_count: this.pageCount,
        page_limit: this.pageLimit,
        visible_duration_ms: Math.round(performance.now() - this.shownAt),
      });
    }

    this.pendingResolve?.(false);
    this.pendingResolve = null;
    this.isOpen = false;
  }
}
